import {
  Asset,
  WorkflowVersionMap,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import { ComponentValidationError } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/component-validations/types';
import ConfigSetting from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentConfigSetting/ConfigSetting';
import DateRangeSetting from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentConfigSetting/DateRangeSetting';
import ImageSettings from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentConfigSetting/ImageSetting';
import MultiSelectSetting from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentConfigSetting/MultiSelectSetting';
import NumberSetting from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentConfigSetting/NumberSetting';
import RadioSetting from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentConfigSetting/RadioSetting';
import SelectSetting from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentConfigSetting/SelectSetting';
import TextSetting from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentConfigSetting/TextSetting';
import ToggleSetting from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentConfigSetting/ToggleSetting';
import TransitionSettings from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentConfigSetting/TransitionSetting';
import {
  ComponentSetting,
  ComponentSettingImage,
  ComponentSettingType,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/types';
import {
  InquiryTemplateVersionStep,
  InquiryTemplateVersionStepUi,
} from 'dashboard/models/inquiry-template-version';
import { safeApply } from 'inquiry/lib/jsonLogic';
import { InquiryIso6391LanguageCode } from 'lib/constants/locales';
import { FieldSchema } from 'lib/fields';
import {
  InputDateComponentConfig,
  StepperComponentConfig,
  UiComponent,
  UiComponentConfig,
  UiComponentConfigType,
  UiImageComponentConfig,
  UiTransitionComponentConfig,
} from 'lib/interfaces/ui-components';
import { assertNever } from 'lib/utils/types';

interface ComponentConfigSettingProps {
  componentSetting: ComponentSetting;
  basePath: string;
  editable: boolean;
  componentConfig: UiComponentConfig['config'];
  componentType: UiComponentConfigType;
  componentPreview: UiComponent;
  currentLocale: InquiryIso6391LanguageCode;
  enabledLocales: InquiryIso6391LanguageCode[];
  stepName: string;
  steps: InquiryTemplateVersionStep[];
  fieldSchema: FieldSchema[];
  workflowVersionMap: WorkflowVersionMap;
  componentValidationErrors: ComponentValidationError[] | undefined;
  isNewComponent: boolean;
  currentStep: InquiryTemplateVersionStepUi;
  updatePathPrefix?: string;
  getStepLocalizedStrings: (locales?: Array<InquiryIso6391LanguageCode>) => Promise<void>;
  handleUpdateComponentConfig: (componentConfigUpdates: Record<string, any>) => void;
  handleImageSelect: (asset: Asset, imageSetting: ComponentSettingImage) => void;
}

const ComponentConfigSetting = ({
  componentSetting,
  componentConfig,
  componentType,
  updatePathPrefix = undefined,
  ...props
}: ComponentConfigSettingProps) => {
  if (
    componentSetting.type !== ComponentSettingType.Transition &&
    safeApply(componentSetting.hidden, componentConfig)
  ) {
    return <></>;
  }

  const sharedProps = {
    componentConfig,
    componentSetting,
    componentType,
    updatePathPrefix,
    ...props,
  };

  switch (componentSetting.type) {
    case ComponentSettingType.DateRange:
      return (
        <DateRangeSetting
          {...props}
          componentSetting={componentSetting}
          componentConfig={componentConfig as InputDateComponentConfig['config']}
        />
      );
    case ComponentSettingType.Transition:
      return (
        <TransitionSettings
          {...sharedProps}
          componentConfig={componentConfig as UiTransitionComponentConfig['config']}
        />
      );
    case ComponentSettingType.Image:
      return (
        <ImageSettings
          {...sharedProps}
          componentSetting={componentSetting as ComponentSettingImage<UiImageComponentConfig>}
          componentConfig={componentConfig as UiImageComponentConfig['config']}
        />
      );
    case ComponentSettingType.Config:
      return (
        <ConfigSetting
          {...sharedProps}
          componentSetting={componentSetting}
          componentConfig={componentConfig as StepperComponentConfig['config']}
        />
      );
    case ComponentSettingType.Select:
      return <SelectSetting {...sharedProps} componentSetting={componentSetting} />;
    case ComponentSettingType.MultiSelect:
      return <MultiSelectSetting {...sharedProps} componentSetting={componentSetting} />;
    case ComponentSettingType.Number:
      return <NumberSetting {...sharedProps} componentSetting={componentSetting} />;
    case ComponentSettingType.Toggle:
      return <ToggleSetting {...sharedProps} componentSetting={componentSetting} />;
    case ComponentSettingType.Radio:
      return <RadioSetting {...sharedProps} componentSetting={componentSetting} />;
    case ComponentSettingType.Text:
      return <TextSetting {...sharedProps} componentSetting={componentSetting} />;
    default:
      assertNever(componentSetting);
  }
};

export default ComponentConfigSetting;
